/**
 * Unique, order-preserving values for Chart of Account column filters.
 * Display rows keep tasklist as checklist IDs, so matching uses a parallel
 * primitive array of task refs (same pattern as Information Manager).
 */

import { canonicalLocationKey } from '../../../utils/locationName';

export const TASK_LINKED_FILTER_FIELD = 'tasklistFilterValues';

export const COA_MIN_FIELD_LENGTH = 2;

export const COA_VALIDATION_MESSAGES = {
  GLCODE_REQUIRED: 'Account No. is required.',
  GLCODE_MIN_LENGTH: `Account No. must be at least ${COA_MIN_FIELD_LENGTH} characters.`,
  TYPE_REQUIRED: 'Type is required.',
  NAME_REQUIRED: 'Account name is required.',
  NAME_MIN_LENGTH: `Account name must be at least ${COA_MIN_FIELD_LENGTH} characters.`,
  CATEGORY_REQUIRED: 'Category is required.',
  CATEGORY_MIN_LENGTH: `Category must be at least ${COA_MIN_FIELD_LENGTH} characters.`,
  SUBCATEGORY_REQUIRED: 'Sub category is required.',
  SUBCATEGORY_MIN_LENGTH: `Sub category must be at least ${COA_MIN_FIELD_LENGTH} characters.`,
  LOCATION_REQUIRED: (label) => `${label} is required.`,
  LOCATION_INVALID: (label) => `Select a valid ${label} from the list.`,
};

export const COA_EMPTY_MESSAGES = {
  LINK_TASKS_NO_DATA: 'No data found',
  LINK_TASKS_PLACEHOLDER: 'Select tasks',
};

export const COA_TOOLTIP_Z_INDEX = 1000;

function uniqueNonEmpty(values) {
  const seen = new Set();
  const result = [];
  (values || []).forEach((value) => {
    if (value === null || value === undefined || value === '') return;
    const key = String(value);
    if (!seen.has(key)) {
      seen.add(key);
      result.push(value);
    }
  });
  return result;
}

export function buildTaskLinkedFilterValues(taskIds, getTask) {
  return uniqueNonEmpty(
    (taskIds || []).map((id) => {
      const task = typeof getTask === 'function' ? getTask(id) : null;
      return task && task.ref ? task.ref : '';
    })
  );
}

export function normalizeChartOfAccountFilterCriteria(criteria) {
  if (!criteria || typeof criteria !== 'object') return criteria;
  const mapped = { ...criteria };
  if (Object.prototype.hasOwnProperty.call(mapped, 'tasklist')) {
    mapped[TASK_LINKED_FILTER_FIELD] = mapped.tasklist;
    delete mapped.tasklist;
  }
  return mapped;
}

function isBlankString(value) {
  return !value || typeof value !== 'string' || !value.trim();
}

function requiredMinLengthError(value, requiredMessage, minLengthMessage, minLength = COA_MIN_FIELD_LENGTH) {
  if (isBlankString(value)) return requiredMessage;
  if (value.trim().length < minLength) return minLengthMessage;
  return '';
}

function hasSelectedLocations(locationId) {
  const selected = Array.isArray(locationId) ? locationId : [];
  return selected.some((item) => item !== null && item !== undefined && String(item).trim() !== '');
}

function locationIdOf(location) {
  if (!location) return '';
  if (typeof location === 'object') return String(location._id || location.id || '');
  return String(location);
}

function normalizeLocationName(value) {
  return canonicalLocationKey(value);
}

function entryExplicitName(entry) {
  if (!entry || typeof entry !== 'object') return '';
  return String(entry.name || entry.description || '').trim();
}

function locationMatchesEntry(location, entry) {
  if (!location) return false;
  const locId = String(location._id || '');
  if (entry && typeof entry === 'object') {
    const explicitName = entryExplicitName(entry);
    if (explicitName) {
      return normalizeLocationName(location.name) === normalizeLocationName(explicitName);
    }
    const entryId = locationIdOf(entry);
    return Boolean(entryId && locId && locId === entryId);
  }
  const value = String(entry ?? '').trim();
  if (!value) return false;
  if (locId && locId === value) return true;
  return normalizeLocationName(location.name) === normalizeLocationName(value);
}

function hasUnresolvedLocationEntries(entries, locations = []) {
  const selected = (Array.isArray(entries) ? entries : []).filter(
    (item) => item !== null && item !== undefined && String(item).trim() !== ''
  );
  return selected.some(
    (entry) => !(locations || []).some((location) => locationMatchesEntry(location, entry))
  );
}

export function chartOfAccountLocationEntries(item = {}) {
  if (Array.isArray(item.location) && item.location.length) return item.location;
  if (Array.isArray(item.locationId) && item.locationId.length) return item.locationId;
  return [];
}

export function locationEntryName(entry, locations = []) {
  if (entry == null || entry === '') return '';
  const explicitName = entryExplicitName(entry);
  if (explicitName) {
    const nameMatch = (locations || []).find(
      (location) => normalizeLocationName(location?.name) === normalizeLocationName(explicitName)
    );
    return nameMatch?.name ? String(nameMatch.name) : explicitName;
  }
  const match = (locations || []).find((location) => locationMatchesEntry(location, entry));
  if (match && match.name) return String(match.name);
  if (typeof entry === 'object') {
    return locationIdOf(entry);
  }
  return String(entry).trim();
}

export function locationEntriesToNames(entries, locations = []) {
  return uniqueNonEmpty((entries || []).map((entry) => locationEntryName(entry, locations)));
}

export function locationEntriesToDocs(entries, locations = []) {
  const selected = Array.isArray(entries) ? entries : [];
  return (locations || []).filter((location) =>
    selected.some((entry) => locationMatchesEntry(location, entry))
  );
}

/**
 * Task refs for a chart-of-account row, in checklist_id order.
 * checklist_id and checklist_id_detail are not guaranteed to share an index,
 * so each id is matched on its own. Entries with no ref are skipped so the
 * first chip is the first task that actually has a reference.
 */
export function linkedTaskRefs(tasklist = [], details = [], getTask) {
  const detailById = new Map();
  uniqueTaskEntries(details).forEach((entry) => {
    const id = taskEntryId(entry);
    if (!id || detailById.has(id)) return;
    detailById.set(id, entry);
  });

  return uniqueNonEmpty(
    uniqueTaskEntries(tasklist).map((entry) => {
      const id = taskEntryId(entry);
      const fromMaster = typeof getTask === 'function' ? getTask(entry) : null;
      if (fromMaster && fromMaster.ref) return fromMaster.ref;
      const detail = id ? detailById.get(id) : null;
      if (detail && detail.ref) return detail.ref;
      if (entry && typeof entry === 'object' && entry.ref) return entry.ref;
      return '';
    })
  );
}

export function taskEntryId(entry) {
  if (entry == null || entry === '') return '';
  if (typeof entry === 'object') return String(entry._id || entry.checklist_id || '');
  return String(entry);
}

export function uniqueTaskEntries(entries = []) {
  const seen = new Set();
  const result = [];
  (entries || []).forEach((entry) => {
    const id = taskEntryId(entry);
    if (!id || seen.has(id)) return;
    seen.add(id);
    result.push(entry);
  });
  return result;
}

export function uniqueTaskIds(entries = []) {
  return uniqueTaskEntries(entries).map((entry) => taskEntryId(entry));
}

export function validateChartOfAccountData(data = {}, locationLabel = 'Location', allowedLocations = []) {
  const errors = {};
  const glcodeError = requiredMinLengthError(
    data.glcode,
    COA_VALIDATION_MESSAGES.GLCODE_REQUIRED,
    COA_VALIDATION_MESSAGES.GLCODE_MIN_LENGTH
  );
  if (glcodeError) errors.glcode = glcodeError;

  if (isBlankString(data.type)) {
    errors.type = COA_VALIDATION_MESSAGES.TYPE_REQUIRED;
  }

  const nameError = requiredMinLengthError(
    data.name,
    COA_VALIDATION_MESSAGES.NAME_REQUIRED,
    COA_VALIDATION_MESSAGES.NAME_MIN_LENGTH
  );
  if (nameError) errors.name = nameError;

  const categoryError = requiredMinLengthError(
    data.category,
    COA_VALIDATION_MESSAGES.CATEGORY_REQUIRED,
    COA_VALIDATION_MESSAGES.CATEGORY_MIN_LENGTH
  );
  if (categoryError) errors.category = categoryError;

  const subcategoryError = requiredMinLengthError(
    data.subcategory,
    COA_VALIDATION_MESSAGES.SUBCATEGORY_REQUIRED,
    COA_VALIDATION_MESSAGES.SUBCATEGORY_MIN_LENGTH
  );
  if (subcategoryError) errors.subcategory = subcategoryError;

  if (!hasSelectedLocations(data.locationId)) {
    errors.locationId = COA_VALIDATION_MESSAGES.LOCATION_REQUIRED(locationLabel);
  } else if (Array.isArray(allowedLocations) && allowedLocations.length) {
    if (hasUnresolvedLocationEntries(data.locationId, allowedLocations)) {
      errors.locationId = COA_VALIDATION_MESSAGES.LOCATION_INVALID(locationLabel);
    }
  }

  return errors;
}
